import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { CircleUserRound, Menu, Phone, X } from 'lucide-react';
import Logo from './Logo';
import { useSiteData } from '../context/SiteDataContext';
import { useAuth } from '../context/AuthContext';
import { firebaseReady } from '../lib/firebase';

const links = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Detailing' },
  { to: '/custom', label: 'Custom Work' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings } = useSiteData();
  const { user, profile } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'border-b border-white/[0.06] bg-night-950/85 backdrop-blur-lg' : 'bg-transparent'
      }`}
    >
      <nav className="container-x flex h-[72px] items-center justify-between">
        <Link to="/" onClick={() => setOpen(false)} aria-label="Apex Car Care home">
          <Logo />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'text-accent-400' : 'text-zinc-300 hover:bg-white/[0.05] hover:text-white'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`}
            className="flex items-center gap-2 text-sm font-medium text-zinc-300 transition-colors hover:text-white"
          >
            <Phone size={15} className="text-accent-400" />
            {settings.phone}
          </a>
          {firebaseReady && (
            <Link
              to={user ? '/account' : '/login'}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-zinc-300 transition-colors hover:bg-white/[0.05] hover:text-white"
              title={user ? 'My Account' : 'Sign In'}
            >
              <CircleUserRound size={17} className="text-accent-400" />
              {user ? (profile?.name?.split(' ')[0] || 'Account') : 'Sign In'}
            </Link>
          )}
          <Link to="/booking" className="btn-primary !px-5 !py-2.5 text-sm">
            Book Now
          </Link>
        </div>

        <button
          className="rounded-lg p-2 text-white lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-white/[0.06] bg-night-950/95 backdrop-blur-lg lg:hidden">
          <div className="container-x flex flex-col gap-1 py-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 font-medium ${
                    isActive ? 'bg-white/[0.05] text-accent-400' : 'text-zinc-300'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {firebaseReady && (
              <NavLink
                to={user ? '/account' : '/login'}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-lg px-4 py-3 font-medium text-zinc-300"
              >
                <CircleUserRound size={17} className="text-accent-400" />
                {user ? 'My Account' : 'Sign In'}
              </NavLink>
            )}
            <Link to="/booking" onClick={() => setOpen(false)} className="btn-primary mt-2">
              Book Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
